// Ported from relay-protocol packages/relay-widget/src/useDepositAddress.ts
// (lines 642-737, plus the optsRef pattern at lines 208-211 and the
// ORDERS_FALLBACK_POLL_MS / ORDERS_DEBOUNCE_MS constants at lines 41-49,
// v0.1.0). Extracted into a standalone hook (Task 5) so both
// RelayDepositContent (gated on catalog via `enabled`) and RelayHistory
// (always enabled) can consume the orders SSE + fallback-polling machinery
// without depending on the rest of useDepositAddress.
import { useEffect, useRef, useState } from 'react'
import type { RelayClient } from '../lib/client'
import type { OrderSummary } from '../lib/types'
import { isAddress } from '../lib/units'

/** Safety-net poll cadence for the orders list (GET /v1/orders?user=) -- the
 * primary refresh signal is the SSE stream (GET /v1/orders/stream?user=),
 * so this only matters when that stream is unavailable or has silently
 * dropped. */
const ORDERS_FALLBACK_POLL_MS = 30_000
/** Coalesces bursts of `orders-changed` stream events (or an event
 * immediately followed by a reconnect) into a single trailing `listOrders`
 * refetch. */
const ORDERS_DEBOUNCE_MS = 300

export interface UseRelayOrdersOptions {
  client: RelayClient
  /** Valid EVM address, or undefined -- undefined clears the list and stops polling. */
  recipient?: string
  /** Gate: wait for truthy before the first fetch (RelayDeposit passes rawCatalog). Default true. */
  enabled?: boolean
  /** Keep the snapshot live with SSE + fallback polling. Default true. */
  live?: boolean
  /** REST safety-net cadence in milliseconds. Defaults to 30s; the open Deposit popup uses 1s
   * so completion still appears promptly when the SSE event is absent. */
  refetchInterval?: number
  /** @deprecated Use `refetchInterval`. Retained for compatibility. */
  fallbackPollMs?: number
  onError?: (e: Error) => void
}

export interface UseRelayOrdersResult {
  /** Every order swept from deposits to this recipient so far, newest-first
   * as served by the API. Kept live unless `live: false` requests one snapshot. */
  orders: OrderSummary[]
  ordersLoading: boolean
  ordersError?: Error
  /** Recipient the current snapshot belongs to. Undefined while a new
   * subscription is waiting for its first accepted fetch, so consumers never
   * baseline a previous recipient's rows as fresh deposits. */
  ordersForRecipient?: string
  /** Increments after every accepted fetch, even when the client returns the
   * same array reference or the request fails. Consumers use this as a
   * low-frequency refresh signal for related read models such as /recovery. */
  ordersRevision: number
  /** True once at least one fetch has COMPLETED (success or failure) for the
   * current (recipient, enabled) subscription; false again when it resets.
   * Distinct from `!ordersLoading`, which is also true BEFORE the first fetch
   * has even started -- consumers that snapshot the list (e.g. the deposit
   * wizard's new-order watch) must wait for this, or they baseline against
   * the initial empty state and misread every existing order as new. */
  ordersInitialized: boolean
}

export function useRelayOrders(opts: UseRelayOrdersOptions): UseRelayOrdersResult {
  // Keep the latest opts (callbacks especially) in a ref so the effect below
  // can read them without needing fresh-identity-triggered re-subscriptions.
  const optsRef = useRef(opts)
  optsRef.current = opts

  const { client, enabled = true, live = true } = opts
  const refetchInterval = opts.refetchInterval ?? opts.fallbackPollMs ?? ORDERS_FALLBACK_POLL_MS
  const recipientKey = opts.recipient && isAddress(opts.recipient) ? opts.recipient : undefined

  // ---- Orders list: SSE-driven refresh, 30s fallback poll -------------------------------------------------
  // One order per deposit the sweeper detects at the standing address, so
  // this refreshes continuously (not scoped to a single order) as long as
  // `recipient` resolves to a valid address -- independent of whether a
  // deposit address has been fetched yet for the *current* origin.
  //
  // GET /v1/orders/stream?user= is a minimal-signal SSE endpoint: it emits
  // `event: connected` on open, `event: orders-changed` whenever this user's
  // orders change, and `: heartbeat` comments -- it never pushes order data
  // itself, so every signal (initial connect, a change event, or a
  // reconnect after a drop) just triggers a normal `listOrders` refetch. The
  // 30s fallback interval is a safety net for a silently-dropped stream and
  // the only refresh mechanism in environments without `EventSource` (SSR,
  // some test runners) -- it replaces the old 3s poll cadence entirely.
  const [orders, setOrders] = useState<OrderSummary[]>([])
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [ordersError, setOrdersError] = useState<Error>()
  const [ordersInitialized, setOrdersInitialized] = useState(false)
  const [ordersForRecipient, setOrdersForRecipient] = useState<string>()
  const [ordersRevision, setOrdersRevision] = useState(0)

  useEffect(() => {
    if (!recipientKey) {
      setOrders([])
      setOrdersError(undefined)
      setOrdersLoading(false)
      setOrdersInitialized(false)
      setOrdersForRecipient(undefined)
      setOrdersRevision(0)
      return
    }

    // Wait for the gate (RelayDeposit's rawCatalog) before firing the FIRST
    // fetch -- this is a short, one-time wait, not a dependency on the
    // *current* origin.
    if (!enabled) {
      setOrders([])
      setOrdersError(undefined)
      setOrdersLoading(false)
      setOrdersInitialized(false)
      setOrdersForRecipient(undefined)
      setOrdersRevision(0)
      return
    }

    let cancelled = false
    let isFirstFetch = true
    let debounceTimer: ReturnType<typeof setTimeout> | undefined

    // Monotonic request-sequence guard: the initial fetch, stream-triggered
    // debounced refetches, and the fallback-poll interval can all have
    // `listOrders` calls in flight at once, and there is no guarantee they
    // resolve in the order they were issued. Each `refetch()` call captures
    // the seq it was issued at; if a newer call has been issued by the time
    // this one resolves, its (now-stale) result is dropped instead of
    // clobbering the fresher data already applied.
    let seq = 0
    let requestsInFlight = 0

    async function refetch() {
      const mySeq = ++seq
      requestsInFlight += 1
      if (isFirstFetch) setOrdersLoading(true)
      try {
        const result = await client.listOrders(recipientKey as string)
        if (cancelled) return
        if (mySeq === seq) {
          setOrders(result)
          setOrdersError(undefined)
        }
      } catch (err) {
        if (cancelled) return
        const e = err instanceof Error ? err : new Error(String(err))
        if (mySeq === seq) {
          setOrdersError(e)
          optsRef.current.onError?.(e)
        }
      } finally {
        requestsInFlight -= 1
        // A reconnect-triggered request can overtake the initial fetch. Only
        // the latest issued request may publish loading/initialization state;
        // otherwise the stale initial request briefly marks an empty snapshot
        // initialized and a deposit watcher treats every existing order as new.
        if (!cancelled && mySeq === seq) {
          setOrdersLoading(false)
          // Success OR failure both count: "a fetch has completed" is the
          // fact consumers wait on, and a failure is reported via ordersError.
          setOrdersInitialized(true)
          setOrdersForRecipient(recipientKey)
          setOrdersRevision((value) => value + 1)
        }
        isFirstFetch = false
      }
    }

    // Coalesces bursts of stream signals (several orders changing at once,
    // or a signal immediately followed by a reconnect) into a single
    // trailing `listOrders` call instead of one per event.
    function debouncedRefetch() {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        debounceTimer = undefined
        if (!cancelled) refetch()
      }, ORDERS_DEBOUNCE_MS)
    }

    refetch() // Unchanged first-fetch / ordersLoading semantics -- fires immediately, not debounced.

    let eventSource: EventSource | undefined
    if (live && typeof EventSource !== 'undefined') {
      eventSource = new EventSource(client.ordersStreamUrl(recipientKey))
      eventSource.addEventListener('orders-changed', debouncedRefetch)
      // Fires on the initial connect AND every auto-reconnect after a drop --
      // either way, a snapshot refetch covers any change that happened while
      // disconnected (or in the brief window before the stream was open).
      eventSource.onopen = () => {
        debouncedRefetch()
      }
    }

    // Safety net: refetches on a fixed cadence regardless of stream health,
    // and is the ONLY refresh mechanism when `EventSource` isn't available.
    const fallbackTimer =
      live && Number.isFinite(refetchInterval) && refetchInterval > 0
        ? setInterval(() => {
            // SSE invalidations may deliberately overtake an old request; the
            // safety poll has no such urgency, so do not add another request
            // while any snapshot fetch is still running.
            if (requestsInFlight === 0) refetch()
          }, refetchInterval)
        : undefined

    return () => {
      cancelled = true
      if (debounceTimer) clearTimeout(debounceTimer)
      if (fallbackTimer) clearInterval(fallbackTimer)
      eventSource?.close()
    }
  }, [client, recipientKey, enabled, live, refetchInterval])

  return {
    orders,
    ordersLoading,
    ordersError,
    ordersInitialized,
    ordersForRecipient,
    ordersRevision,
  }
}
